import type { GitDependencyReference, ActionProperties, IProcessResource, PackageReference, StepPackageInputs, TriggerResource, GitRefResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useAccountsFromContext, useRefreshAccountsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessAccountsContextProvider";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { useGitCredentialsFromContext, useRefreshGitCredentialsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessGitCredentialsContextProvider";
import type { RunOn } from "~/areas/projects/components/Process/types";
import { useOptionalProjectContext } from "~/areas/projects/context";
import { ActionPropertiesEditor } from "~/components/ActionPropertiesEditor/ActionPropertiesEditor";
import { enforceNewActionFeatures } from "~/components/ActionPropertiesEditor/enforceNewActionFeatures";
import type { ActionEditProps, ActionPlugin, AdditionalActions } from "~/components/Actions/pluginRegistry";
import type { ProjectStepInputDependencies } from "~/components/StepPackageEditor/StepInputDependencies";
import type { FieldErrors } from "../DataBaseComponent/Errors";
interface ProjectActionEditorProps {
    deploymentActionSlug: string;
    plugin: ActionPlugin;
    inputs: StepPackageInputs;
    properties: ActionProperties;
    packages: Array<PackageReference>;
    gitDependencies?: GitDependencyReference[];
    runOn: RunOn;
    additionalActions: AdditionalActions;
    errors: FieldErrors | undefined; // only used for shouldComponentUpdate
    busy: Promise<unknown> | boolean | undefined;
    expandedByDefault: boolean;
    editor: React.ComponentType<ActionEditProps>;
    getFieldError(field: string): string;
    setInputs(inputs: StepPackageInputs, callback?: () => void): void;
    setProperties(properties: Partial<ActionProperties>, initialise?: boolean, callback?: () => void): void;
    setPackages(packages: Array<PackageReference>, initialise?: boolean): void;
    setGitDependencies?(gitDependencies: GitDependencyReference[], initialise?: boolean): void;
    doBusyTask(action: () => Promise<void>): Promise<boolean>;
    refreshRunOn(): void;
    getProcessResource(): Readonly<IProcessResource>;
    isNew: boolean;
    projectTriggers: TriggerResource[];
    gitRefResource: GitRefResource | undefined;
    isStepFromProcessTemplate?: boolean;
}
export function ProjectActionPropertiesEditor(props: ProjectActionEditorProps) {
    const { setPackages, packages } = props;
    const { setGitDependencies, gitDependencies } = props;
    const { gitRefResource: gitRef } = props;
    const projectContext = useOptionalProjectContext();
    const processContext = useProcessContext();
    useEffect(() => {
        const properties = { ...props.properties };
        const newActionFeatures = enforceNewActionFeatures(props.plugin, properties, props.isNew, false);
        if (newActionFeatures.length > 0) {
            // We don't assign empty features, or this causes existing steps to trigger the "unsaved changes" dialog.
            properties["Octopus.Action.EnabledFeatures"] = newActionFeatures;
        }
        props.setProperties(properties, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const projectId = projectContext?.state.model.Id;
    const project = projectContext?.state.model;
    const processType = processContext.selectors.getProcessType();
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const accounts = useAccountsFromContext();
    const refreshAccounts = useRefreshAccountsFromContext();
    const gitCredentials = useGitCredentialsFromContext();
    const refreshGitCredentials = useRefreshGitCredentialsFromContext();
    const stableSetPackages = useCallback((getUpdatedPackages: (prev: Array<PackageReference>) => Array<PackageReference>) => setPackages(getUpdatedPackages(packages)), [packages, setPackages]);
    const stableSetGitDependencies = useCallback((getGitDependencies: (prev: GitDependencyReference[]) => GitDependencyReference[]) => setGitDependencies?.(getGitDependencies?.(gitDependencies ?? [])), [gitDependencies, setGitDependencies]);
    const stableRefreshFeeds = useCallback(async () => {
        await refreshFeeds();
    }, [refreshFeeds]);
    const stableRefreshAccounts = useCallback(async () => {
        await refreshAccounts();
    }, [refreshAccounts]);
    const inputDependencies = useMemo<ProjectStepInputDependencies>(() => ({
        projectId,
        gitRef,
        project,
        processType,
        feeds,
        refreshFeeds: stableRefreshFeeds,
        setPackages: stableSetPackages,
        setGitDependencies: stableSetGitDependencies,
        accounts,
        refreshAccounts: stableRefreshAccounts,
        gitCredentials: gitCredentials,
        refreshGitCredentials: refreshGitCredentials,
        deploymentActionSlug: props.deploymentActionSlug,
        projectTriggers: props.projectTriggers,
    }), [projectId, gitRef, project, processType, feeds, stableRefreshFeeds, stableSetPackages, stableSetGitDependencies, accounts, stableRefreshAccounts, gitCredentials, refreshGitCredentials, props.deploymentActionSlug, props.projectTriggers]);
    if (!props.plugin.editSections) {
        throw new Error(`Plugin ${props.plugin.actionType} is missing 'Edit' property`);
    }
    return (<ActionPropertiesEditor inputDependencies={inputDependencies} plugin={props.plugin} editor={props.editor} inputs={props.inputs} properties={props.properties} packages={props.packages} gitDependencies={props.gitDependencies} doBusyTask={props.doBusyTask} busy={props.busy} runOn={props.runOn} setInputs={props.setInputs} setProperties={props.setProperties} setPackages={props.setPackages} setGitDependencies={props.setGitDependencies} additionalActions={props.additionalActions} getFieldError={props.getFieldError} errors={props.errors} expandedByDefault={props.expandedByDefault} refreshRunOn={props.refreshRunOn} getProcessResource={props.getProcessResource} isNew={props.isNew} isStepFromProcessTemplate={props.isStepFromProcessTemplate}/>);
}
